import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { PrimaryButton, SelectOptionValue, TopBar } from '@luminalearning/components';
import env from 'src/env/index';
import { locales } from 'src/lang/locales';
import useScreenSize from 'src/hooks/useScreenSize';
import { MenuIcon } from '@heroicons/react/solid';
import useLanguageChange from 'src/hooks/useLanguageChange';
import { useQuery, useReactiveVar } from '@apollo/client';
import GET_LOCALE from 'src/graphql/queries/intlQuery';
import { useNavigate } from 'react-router-dom';
import { activePractitionerDetails } from 'src/graphql';
import USER_TOKEN from 'src/graphql/queries/authQuery';
import { loginLuminaOnline } from 'src/services/auth';
import updateLocale from 'src/utils/updateLocale';

const Topnav = ({
    isMobileNavOpen,
    setIsMobileNavOpen,
    isHideSidebar,
}: {
    isMobileNavOpen: boolean;
    setIsMobileNavOpen: (isOpen: boolean) => void;
    isHideSidebar: boolean;
}) => {
    const ssoVersion = localStorage.getItem('ssoVersion');
    const intl = useIntl();
    const changeLanguage = useLanguageChange();
    const navigate = useNavigate();
    const { data: locale } = useQuery(GET_LOCALE);
    const { data: token } = useQuery(USER_TOKEN);
    const practitionerDetails = useReactiveVar(activePractitionerDetails);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [avatar, setAvatar] = useState();
    const [isNavigating, setIsNavigating] = useState(false);
    const [barSize, setBarSize] = useState<'Large' | 'Small' | 'Medium' | undefined>('Medium');
    const [width, _] = useScreenSize();

    const handleLanguageChange = (value: SelectOptionValue | null) => {
        if (value !== null) {
            updateLocale(value.id as string);
            changeLanguage(value.id as string);
            localStorage.setItem('locale', value.id as string);
        }
    };

    useEffect(() => {
        // sets the top bar between it's different sizes depending on the screen width
        if (width > 1400 && barSize !== 'Large') {
            setBarSize('Large');
        }
        if (width < 1400 && barSize !== 'Small') {
            setBarSize('Small');
        }
    }, [width, barSize]);

    useEffect(() => {
        if (practitionerDetails) {
            setName(`${practitionerDetails.firstName} ${practitionerDetails.lastName}`);
            setEmail(practitionerDetails.email);
        }
    }, [practitionerDetails]);

    useEffect(() => {
        if (token) {
            setAvatar(token?.picture || undefined);
        }
    }, [token]);

    const navigateToSupportPage = async () => {
        if (practitionerDetails) {
            setIsNavigating(true);
            await loginLuminaOnline(practitionerDetails.id);
            window.location.href = `${env.luminaOnlineUrl}/support`;
            setIsNavigating(false);
        }
    };

    return (
        <>
            <div className='py-4 relative bg-primary lg:hidden xl:hidden'>
                {!isHideSidebar
                    && <PrimaryButton
                        text={intl.formatMessage({
                            id: 'LS_MENU',
                            defaultMessage: 'Menu',
                        })}
                        onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                        // eslint-disable-next-line max-len
                        className='absolute bg-gradient-to-r from-gradients-primary-start to-gradients-primary-end top-4 -left-6 z-10'
                        icon={MenuIcon}
                    />
                }
                <a href={env.luminaOnlineUrl}>
                    <img
                        src={'/img/LuminaLearningLogo.png'}
                        alt={intl.formatMessage({
                            id: 'LC_LUMINA_LEARNING',
                            defaultMessage: 'Lumina Learning',
                        })}
                        className='mx-auto h-10'
                    />
                </a>
            </div>
            <TopBar
                size={barSize}
                className='hidden lg:block'
                handleLogin={() => {
                    // eslint-disable-next-line max-len
                    window.location.href = `${ssoVersion === '2' ? env.ssoV2Url : env.ssoV1Url}/login?returnUrl=${encodeURIComponent(window.location.href)}`;
                }}
                handleSignOut={() => navigate('/logout')}
                handleProfileClick={() => {
                    window.location.href = `${ssoVersion === '2' ? env.ssoV2Url : env.ssoV1Url}/portal/profile`;
                }}
                handleSupportClick={() => {
                    if (!isNavigating) {
                        navigateToSupportPage();
                    }
                }}
                handlePortalClick={`${ssoVersion === '2' ? env.ssoV2Url : env.ssoV1Url}/portal`}
                language={{
                    id: locale.appLocale,
                    value: (locales[locale.appLocale]?.localName || locales[locale.appLocale].englishName),
                }}
                userName={name}
                userPicture={avatar}
                userEmail={email}
                languages={
                    Object.keys(locales).map(
                        (localeKey) => ({
                            id: localeKey,
                            value: (locales[localeKey]?.localName || locales[localeKey].englishName),
                            isDisabled: false,
                        })
                    )
                }
                setLanguage={handleLanguageChange}
            />
        </>
    );
};
export default Topnav;